import { useState, useEffect, useMemo, Fragment } from 'react'
import {
  ShopBanner,
  Steps,
  resortSteps,
  CheckoutResortStep1,
  CheckoutResortStep2,
  CheckoutResortStep3,
  CheckoutSuccess,
  IntentPopup,
} from '../components/checkout'
import PropTypes from 'prop-types'
import md5 from 'md5'
import { useSelectedRoom, useDueNow } from '../hooks'
import { useSelector, useDispatch } from 'react-redux'
import TrustPilot from '../components/checkout/TrustPilot'
import CourseDescriptionPopup from '../components/popups/CourseDescriptionPopup'
import { setCourseId, setInclusions } from '../actions/resort-checkout-actions'
import { setShopInfo } from '../actions/bookings-actions'
import store from 'store2'
import API from 'api'
import { getAffiliateId, isStaff, makeKey, parseQuery, roundPrice } from '../utils'
import Loader from '../components/helpers/Loader'
import { showLoader } from '../actions/main-actions'
import { toast } from 'react-toastify'
import { trackEcommerce, trackEvent, trackGA4Ecommerce } from '../gtm'
import { setGtmDataForSelectedInclusions } from '../hooks/useResortInclusions'
import { convertCurrency } from '../components/helpers'
import dayjs from 'dayjs'

CheckoutResortPage.propTypes = {
  id: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  shopTitle: PropTypes.string.isRequired,
  shopUrl: PropTypes.string.isRequired,
  shopPicture: PropTypes.string.isRequired,
  availablePackageKinds: PropTypes.array.isRequired,
  hasStudentCourses: PropTypes.bool.isRequired,
  isFixedPackage: PropTypes.bool.isRequired,
  rating: PropTypes.number,
  numberOfReviews: PropTypes.number.isRequired,
  shopLocationTitle: PropTypes.string.isRequired,
  savedOrder: PropTypes.bool,
  locationSlug: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
}

const query = parseQuery()
// TODO Rename it later
const affiliateId = query.aid || getAffiliateId()?.aid
const referrer = query.referrer || store.session('diviac.referrer')
const uuid = query.order_uuid || store.session('diviac.order_uuid')

if(uuid) {
  store.session('diviac.order_uuid', uuid)
}
if(referrer) {
  store.session('diviac.referrer', referrer)
}

export default function CheckoutResortPage({
  id,
  slug,
  shopTitle,
  locationSlug,
  rating,
  numberOfReviews,
  shopUrl,
  shopPicture,
  shopLocationTitle,
  availablePackageKinds,
  hasStudentCourses,
  isFixedPackage,
  currency,
  savedOrder,
  countryCode,
}) {
  const dispatch = useDispatch()
  const [order, setOrder] = useState()
  const [selectedTripData, setSelectedTripData] = useState({})
  const selectedRoom = useSelectedRoom()
  const { dueNowData, getDueNowData, setDue } = useDueNow()
  const isLoading = useSelector((state) => state.pageLoader)
  const inclusions = useSelector((state) => state.checkoutInclusions)
  const selectedCourseId = useSelector((state) => state.selectedCourseId)
  const [step, setStep] = useState(1)
  const [ratePrice, setRatePrice] = useState(0)
  const [inclusionsPrice, setInclusionsPrice] = useState(0)
  const divingInclusionsPrice = useMemo(() => {
    let divePrice = 0
    if(inclusions.diverInclusions && !isFixedPackage) {
      divePrice = Object.values(inclusions.diverInclusions).reduce(
        (acc, val) => {
          acc = acc + val.price
          return acc
        },
        0,
      )
    }
    return divePrice
  }, [inclusions])
  useEffect(() => {
    const { rate, room, packages } = selectedRoom
    if((rate || packages) && room) {
      setStep(2)
    }
  }, [selectedRoom])

  useEffect(() => {
    window.scrollTo(0, 0)
    if(step === 1) {
      dispatch(setInclusions({}))
    }
    // Clear due now data
    if(step !== 3) {
      setDue()
    }
    window.onpopstate = function() {
      if(step === 1 || step === 4) {
        window.history.back()
        window.onpopstate = undefined
      } else {
        setStep((step) => step - 1 || 1)

        window.history.pushState('back', null, null)
      }
    }
  }, [step])
  useEffect(() => {
    trackEvent({
      page_content_type: 'Travel - Vacations - Booking',
      business_type: 'Dive resort',
    })
    dispatch(showLoader(true))
    dispatch(
      setShopInfo({
        shopKind: 20,
        slug,
      }),
    )
    if(!savedOrder) {
      dispatch(showLoader(false))
    }
    if(referrer && uuid && isStaff()) {
      toast.error('ORDER EDIT MODE HAS BEEN ACTIVATED!', {
        position: toast.POSITION.TOP_CENTER,
      })
    }
  }, [])

  const totalPrice = useMemo(() => {
    return ratePrice + divingInclusionsPrice + inclusionsPrice
  }, [ratePrice, divingInclusionsPrice, inclusionsPrice])

  const closePopup = () => dispatch(setCourseId(null))
  const saveSelectedInclusions = (items, resortData, inclusionsForGTM) => {
    let selectedInclusions = [...items]
    let totalDivesSelected = 0

    if(inclusions.diverInclusions && !isFixedPackage) {
      Object.values(inclusions.diverInclusions).forEach((val) => {
        totalDivesSelected += val.numberOfInstances
        selectedInclusions.push(val)
      })
    }
    const payedPerDive = selectedInclusions.filter(
      (incl) => incl.payedPer === 20,
    )
    if(payedPerDive?.length) {
      payedPerDive.forEach((incl) => {
        incl.quantity = totalDivesSelected
        incl.amount = totalDivesSelected * (incl.price || 0)
      })
    }
    getDueNowData(id, selectedInclusions, resortData)
      .then(() => {
        const {
          gtmGA4Products,
          gtmProducts,
          value,
        } = setGtmDataForSelectedInclusions(
          inclusionsForGTM,
          currency,
          shopTitle,
          id,
        )
        setSelectedTripData(resortData)
        setStep(3)

        trackGA4Ecommerce(
          'begin_checkout',
          {
            currency: 'USD',
            value: Number(value),
            items: [...resortData.ga4EcommerceProduct, ...gtmGA4Products],
          },
          'PADI Travel - Dive Resorts',
        )
        store.session('', uuid)
        trackEcommerce(
          'checkout_step_review',
          {
            checkout: {
              actionField: {
                step: 3,
              },
              products: [...resortData.ecommerceProduct, ...gtmProducts],
            },
          },
          'USD',
        )
      })
      .catch(() => {
        dispatch(showLoader(false))
      })
  }
  const onVoucherAdd = (coupon) => {
    dispatch(showLoader(true))
    getDueNowData(
      id,
      dueNowData.selectedInclusions,
      selectedTripData,
      coupon.id,
    ).then(() => {
      dispatch(showLoader(false))
    })
  }

  const onCouponRemove = () => {
    dispatch(showLoader(true))
    getDueNowData(id, dueNowData.selectedInclusions, selectedTripData).then(
      () => {
        dispatch(showLoader(false))
      },
    )
  }

  const onCheckoutFormSubmit = (values) => {
    const recaptcha = window.grecaptcha && window.grecaptcha.getResponse()
    if(!recaptcha) {
      return toast.error('Captcha is required', { position: 'top-center' })
    }

    const totalUSDPrice = roundPrice(
      convertCurrency(totalPrice, currency, 'USD'),
    )
    const duration = dayjs(dueNowData.dateTo).diff(dueNowData.dateFrom, 'day')
    values.recaptcha = makeKey(recaptcha)
    dispatch(showLoader(true))
    if(uuid) {
      API(`checkout/orders/${uuid}/update/`)
        .put({
          ...values,
          ...dueNowData.payload,
          referrer,
          uuid,
          affiliateId: values.affiliateId || affiliateId,
        })
        .then((data) => {
          if(referrer) {
            window.location.href = referrer
          } else {
            data.orderUpdated = true
            setOrder(data)
            setStep(4)
          }
          store.session.remove('diviac.referrer')
          store.session.remove('diviac.order_uuid')
          store.session.set('exitPopup', true)
        })
        .catch(() => window.grecaptcha.reset())
        .finally(() => dispatch(showLoader(false)))
    } else {
      API(isStaff() ? 'checkout/staff/' : 'checkout/')
        .post({
          ...dueNowData.payload,
          ...values,
          affiliateId: values.affiliateId || affiliateId,
        })
        .then((data) => {
          setOrder(data)
          setStep(4)
          store.session.set('exitPopup', true)

          trackEvent({
            checkout_order_id: data.reference,
            user_email_md5: data.email && md5(data.email),
            checkout_order_amount: totalUSDPrice,
          })

          trackGA4Ecommerce(
            'purchase',
            {
              currency: 'USD',
              value: Number(convertCurrency(dueNowData?.taxFreeAmount, dueNowData?.currency, 'USD').toFixed(2)),
              transaction_id: data.reference,
              tax: dueNowData?.taxesAmount,
              payment_type: '',
              number_of_divers: dueNowData.payload.roomParams.divers,
              number_of_nights: duration,
              items: store.session.get('ecommerce'),
            },
            'PADI Travel - Dive Resorts',
          )

          if(isStaff()) {
            window.location.href = `${process.env.INTRANET_URL}/booking-list/?order_id=${data.id}&tab=Internal`
          }
        })
        .catch(() => window.grecaptcha.reset())
        .finally(() => dispatch(showLoader(false)))
    }
  }

  if(step === 4) {
    return <CheckoutSuccess order={order} />
  }

  return (
    <section className='checkout-dr'>
      <Loader isLoading={isLoading} fixed size='big' />
      <ShopBanner title={shopTitle} url={shopUrl} image={shopPicture} />
      <Steps
        steps={resortSteps}
        currentStep={step}
        currency={currency}
        setStep={setStep}
        totalPrice={totalPrice}
        showPrice
        dueNowData={dueNowData}
      />
      <div className='content'>
        {step === 1 && (
          <CheckoutResortStep1
            shopSlug={slug}
            shopId={id}
            shopTitle={shopTitle}
            availablePackageKinds={availablePackageKinds}
            hasStudentCourses={hasStudentCourses}
            isFixedPackage={isFixedPackage}
          />
        )}
        {step === 2 && (
          <CheckoutResortStep2
            roomId={selectedRoom.room}
            rate={selectedRoom.rate}
            mealPlan={selectedRoom.mealPlan}
            shopId={id}
            packages={selectedRoom.packages}
            shopTitle={shopTitle}
            isFixedPackage={isFixedPackage}
            setRatePrice={setRatePrice}
            shopSlug={slug}
            setStep={setStep}
            setInclusionsPrice={setInclusionsPrice}
            saveSelectedInclusions={saveSelectedInclusions}
          />
        )}
        {step === 3 && (
          <Fragment>
            <CheckoutResortStep3
              dueNowData={dueNowData}
              id={id}
              slug={slug}
              shopTitle={shopTitle}
              rating={rating}
              numberOfReviews={numberOfReviews}
              isFixedPackage={isFixedPackage}
              shopPicture={shopPicture}
              shopLocationTitle={shopLocationTitle}
              selectedTripData={selectedTripData}
              packagePrice={ratePrice + divingInclusionsPrice}
              shopLocationSlug={locationSlug}
              onCheckoutFormSubmit={onCheckoutFormSubmit}
              currency={currency}
              onVoucherAdd={onVoucherAdd}
              onCouponRemove={onCouponRemove}
              orderEdit={!!uuid}
              countryCode={countryCode}
            />
            <IntentPopup
              shopTitle={shopTitle}
              dueNowData={dueNowData.payload}
              roomTitle={selectedTripData.roomTitle}
              countryCode={countryCode}
            />
          </Fragment>
        )}
        <TrustPilot />

        <CourseDescriptionPopup
          id={selectedCourseId}
          isOpen={!!selectedCourseId}
          onClose={closePopup}
          slug={slug}
        />
      </div>
    </section>
  )
}
